import axios from "axios";
import {AgentBasicDto} from "../dto/AgentBasicDto";
import {AgentUiSettingsDto} from "../dto/AgentUiSettingsDto";
import {PlaybookExampleDto} from "../dto/PlaybookExampleDto";

export const getAllAgents = async (token: string) => {
    try {
        return await axios.get('/api/admin/agents', {headers: {Authorization: `Bearer ${token}`}});
    } catch (error) {
        throw error;
    }
}

export const getUserAgents = async (token: string) => {
    try {
        return await axios.get('/api/agents', {headers: {Authorization: `Bearer ${token}`}});
    } catch (error) {
        throw error;
    }
}

export const createAgent = async (token: string) => {
    try {
        return await axios.post('/api/agents/create', {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
    } catch (error) {
        throw error;
    }
}

export const getSnippet = async (agentId: string, token: string) => {
    try {
        return await axios.get(`/api/agents/${agentId}/chat/snippet?t=${Date.now()}`,
            { headers: {Authorization: `Bearer ${token}`}}
        );
    } catch (error: any) {
        console.error('Error:', error);
        throw error;
    }
}

export const getAgentBasic = async (agentId: string, token: string) => {
    try {
        return await axios.get(
            `/api/agents/${agentId}/basic`,
            { headers: {Authorization: `Bearer ${token}`}}
        );
    } catch (error) {
        throw error;
    }
}

export const putAgentBasic = async (agentId: string, agentBasicDto: AgentBasicDto, token: string) => {
    try {
        return await axios.put(
            `/api/agents/${agentId}/basic`,
            agentBasicDto,
            {
                headers: { Authorization: `Bearer ${token}` }
            }
        );
    } catch (error) {
        throw error;
    }
}

export const getAgentUiSettings = async (agentId: string, token: string) => {
    try {
        return await axios.get(
            `/api/agents/${agentId}/ui-settings`,
            { headers: {Authorization: `Bearer ${token}`}}
        );
    } catch (error) {
        throw error;
    }
}

export const putAgentUiSettings = async (agentId: string, agentUiSettingsDto: AgentUiSettingsDto, token: string) => {
    try {
        return await axios.put(
            `/api/agents/${agentId}/ui-settings`,
            agentUiSettingsDto,
            {
                headers: { Authorization: `Bearer ${token}` }
            }
        );
    } catch (error) {
        throw error;
    }
}

export const fetchUiSettingsPreview = async (agentId: string, token: string, settings: AgentUiSettingsDto) => {
    try {
        return await axios.post(
            `/api/agents/${agentId}/ui-settings/preview`,
            settings,
            {
                headers: {Authorization: `Bearer ${token}`}
            }
        );
    } catch (error) {
        throw error;
    }
}

export const getAgentById = async (agentId: string, token: string) => {
    try {
        return await axios.get(`/api/agents/${agentId}`, {headers: {Authorization: `Bearer ${token}`}});
    } catch (error) {
        console.error(`Error fetching agent with ID ${agentId}:`, error);
        throw error;
    }
};

export const getAgentFiles = async (agentId: string, token: string) => {
    try {
        return await axios.get(`/api/agents/${agentId}/files`, {headers: {Authorization: `Bearer ${token}`}});
    } catch (error) {
        console.error(`Error loading files for agent ${agentId}:`, error);
        throw error;
    }
};

export const uploadAgentFile = async (agentId: string, file: File, token: string) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
        return await axios.post(`/api/agents/${agentId}/files`, formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
    } catch (error) {
        console.error(`Error uploading file for agent ${agentId}:`, error);
        throw error;
    }
};

export const sendQuestion = async (agentId: string, question: string, token: string, sessionId: string | undefined) => {
    try {
        return await axios.post(
            `/api/agents/${agentId}/chat/ask`,
            {
                question: question,
                sessionId: sessionId
            },
            {
                headers: { Authorization: `Bearer ${token}` }
            }
        );
    } catch (error) {
        throw error;
    }
}

export const deleteAgentFile = async (agentId: string, fileId: string, token: string) => {
    try {
        return await axios.delete(
            `/api/agents/${agentId}/files`,
            {
                headers: { Authorization: `Bearer ${token}` },
                data: fileId,
            }
        );
    } catch (error) {
        console.error(`Error deleting file for agent ${agentId}:`, error);
        throw error;
    }
}

export const getAgentConversations = async (agentId: string, token: string) => {
    try {
        return await axios.get(`/api/agents/${agentId}/conversations`, {headers: {Authorization: `Bearer ${token}`}});
    } catch (error) {
        console.error(`Error loading conversations for agent ${agentId}:`, error);
        throw error;
    }
};

export const getConversation = async (agentId: string, conversationId: string, token: string) => {
    try {
        return await axios.get(`/api/agents/${agentId}/conversations/${conversationId}`, {headers: {Authorization: `Bearer ${token}`}});
    } catch (error) {
        console.error(`Error loading conversation ${conversationId} for agent ${agentId}:`, error);
        throw error;
    }
};

export const deleteConversation = async (agentId: string, conversationId: string, token: string) => {
    try {
        return await axios.delete(
            `/api/agents/${agentId}/conversations/${conversationId}`,
            {
                headers: { Authorization: `Bearer ${token}` },
                data: conversationId,
            }
        );
    } catch (error) {
        console.error(`Error deleting conversation for agent ${agentId}:`, error);
        throw error;
    }
}

export const getPlaybookExamples = async (agentId: string, playbookId: string, token: string) => {
    try {
        return await axios.get(`/api/agents/${agentId}/playbooks/${playbookId}/examples`, {headers: {Authorization: `Bearer ${token}`}});
    } catch (error) {
        console.error(`Error loading examples for playbook ${playbookId}:`, error);
        throw error;
    }
};

export const putPlaybookExample = async (agentId: string, playbookId: string, token: string, exampleId: string, playbookExample: PlaybookExampleDto) => {
    try {
        return await axios.put(
            `/api/agents/${agentId}/playbooks/${playbookId}/examples`,
            playbookExample,
            {
                params: {exampleId: exampleId},
                headers: { Authorization: `Bearer ${token}` }
            }
        );
    } catch (error) {
        console.error(`Error saving example for playbook ${playbookId}:`, error);
        throw error;
    }
};

export const postPlaybookExample = async (agentId: string, playbookId: string, token: string, playbookExample: PlaybookExampleDto) => {
    try {
        return await axios.post(
            `/api/agents/${agentId}/playbooks/${playbookId}/examples`,
            playbookExample,
            {
                headers: { Authorization: `Bearer ${token}` }
            }
        );
    } catch (error) {
        console.error(`Error saving example for playbook ${playbookId}:`, error);
        throw error;
    }
};

export const deletePlaybookExample = async (agentId: string, playbookId: string, token: string, exampleId: string) => {
    try {
        return await axios.delete(
            `/api/agents/${agentId}/playbooks/${playbookId}/examples`,
            {
                params: {exampleId: exampleId},
                headers: { Authorization: `Bearer ${token}` }
            }
        );
    } catch (error) {
        console.error(`Error deleting example for playbook ${playbookId}:`, error);
        throw error;
    }
};

export const activateMessengerIntegrationForAgent = async (agentId: string, token: string) => {
    try {
        return await axios.post(
            `/api/agents/${agentId}/messenger-activate`,
            {},
            {
                headers: { Authorization: `Bearer ${token}` }
            }
        );
    } catch (error) {
        console.error(`Error activating agent ${agentId}:`, error);
        throw error;
    }
};
